import type { WasteSenseMapSite, WasteSenseReport } from "./types";

/**
 * Magic fixture id that triggers the deterministic random generator
 * instead of looking up a static JSON file. Paired with a `seed`
 * argument (sourced from the `?seed=` URL param) for reproducibility.
 *
 * Exposed as a constant so callers can do
 * `fixture === RANDOM_FIXTURE_ID` instead of stringly-typing it.
 */
export const RANDOM_FIXTURE_ID = "__random__";

/**
 * Catalogue entry describing one selectable WasteSense report.
 *
 * The `fixture` value is what gets passed in the `?fixture=` query
 * param on the detail route. Keep these stable — bookmarkable URLs.
 *
 * Note: there is no `payload` field. The fixture payloads are large
 * (multiple megabytes of JSON) and would otherwise inflate the
 * shared bundle for every screen that just needs the catalogue
 * listing. The actual JSON is loaded on demand by `getWasteSenseReport`
 * via a per-fixture dynamic `import()`, which the bundler splits
 * into its own chunk.
 */
export interface WasteSenseFixtureEntry {
  /** Filename used as the `?fixture=` query param value (with extension). */
  fixture: string;
  /** Tenant id this fixture represents. */
  tenantId: string;
  /** Tenant display name shown in the list and report header. */
  tenantName: string;
}

/**
 * Loader functions for each fixture.
 *
 * Each entry MUST contain a literal `import("./fixtures/<name>.json")`
 * call — Metro and Webpack both require the import path to be
 * statically analysable in order to pre-create the on-demand chunk.
 * Generic helpers like `(name) => import(\`./fixtures/${name}.json\`)`
 * defeat that and pull every fixture into the parent bundle.
 *
 * Add a new fixture by adding both a registry entry below AND a
 * loader here, keyed by the same `fixture` filename.
 */
const FIXTURE_LOADERS: Readonly<Record<string, () => Promise<{ default: WasteSenseReport }>>> = {
  "merri-bek-city-council.json": () =>
    import("./fixtures/merri-bek-city-council.json") as unknown as Promise<{ default: WasteSenseReport }>,
  "midcoast-council.json": () =>
    import("./fixtures/midcoast-council.json") as unknown as Promise<{ default: WasteSenseReport }>,
  "st-philips.json": () => import("./fixtures/st-philips.json") as unknown as Promise<{ default: WasteSenseReport }>,
};

/**
 * Catalogue of available fixtures — payload-free. The list view and
 * the detail screen both consume this to render their pickers and
 * enrich loaded payloads with tenant metadata. Order here is the
 * order shown in the picker.
 *
 * All fixtures should share `schema_version: "wastesense-report-data-v1"`
 * — when a new schema arrives, add a discriminator and a new code path
 * rather than mutating this list.
 */
const FIXTURE_REGISTRY: readonly WasteSenseFixtureEntry[] = [
  {
    fixture: "merri-bek-city-council.json",
    tenantId: "merri-bek-city-council",
    tenantName: "Merri-bek City Council",
  },
  {
    fixture: "midcoast-council.json",
    tenantId: "midcoast-council",
    tenantName: "MidCoast Council",
  },
  {
    fixture: "st-philips.json",
    tenantId: "st-philips",
    tenantName: "St Philips",
  },
];

const FIXTURE_BY_NAME: ReadonlyMap<string, WasteSenseFixtureEntry> = new Map(
  FIXTURE_REGISTRY.map((entry) => [entry.fixture, entry]),
);

/**
 * Thrown when a fixture is requested by name and not registered.
 *
 * The detail screen catches this and shows a clean error state
 * rather than a generic "something went wrong".
 */
export class WasteSenseFixtureNotFoundError extends Error {
  constructor(fixture: string) {
    super(`Unknown WasteSense fixture: ${fixture}`);
    this.name = "WasteSenseFixtureNotFoundError";
  }
}

/** Returns every fixture currently available, in registration order. */
export function listWasteSenseFixtures(): readonly WasteSenseFixtureEntry[] {
  return FIXTURE_REGISTRY;
}

/**
 * Loads a fixture's JSON payload via dynamic `import()`. Subsequent
 * calls for the same fixture resolve from the bundler's module
 * cache, so the two consumers below (`getWasteSenseReport` and
 * `getWasteSenseSiteLocations`) only pay the network/disk cost once
 * per session even though they're called in parallel by the report
 * screen.
 */
async function loadFixturePayload(fixture: string): Promise<WasteSenseReport> {
  const loader = FIXTURE_LOADERS[fixture];
  if (!loader) {
    throw new WasteSenseFixtureNotFoundError(fixture);
  }
  const mod = await loader();
  return mod.default;
}

/**
 * Fetches a WasteSense report by fixture name (e.g. `merri-bek-city-council.json`).
 *
 * Throws `WasteSenseFixtureNotFoundError` when the fixture isn't
 * registered, and `Error` when no fixture is supplied at all. Adds a
 * randomised 0.5–1.5s delay to mimic a real network round-trip; when
 * the real API exists, swap the body for a real `fetch` keyed by
 * fixture/tenant.
 *
 * Injects a `tenant` block into the returned payload from the registry
 * — the upstream JSON files don't currently carry one, but downstream
 * consumers expect `data.tenant.{id,name}` to be set.
 */
export async function getWasteSenseReport(fixture: string | undefined, seed?: string): Promise<WasteSenseReport> {
  const minMs = 500;
  const maxMs = 1500;
  const delay = minMs + Math.random() * (maxMs - minMs);
  await new Promise<void>((resolve) => setTimeout(resolve, delay));

  if (!fixture) {
    throw new Error("No fixture specified");
  }

  if (fixture === RANDOM_FIXTURE_ID) {
    /**
     * The random generator is also dynamically imported — it pulls in
     * a non-trivial chunk of templates/anchors that nothing else in
     * the app uses, so we keep it out of the catalogue page's bundle.
     */
    const { generateRandomWasteSenseReport } = await import("./random/generator");
    return generateRandomWasteSenseReport(seed ?? "");
  }

  const entry = FIXTURE_BY_NAME.get(fixture);
  if (!entry) {
    throw new WasteSenseFixtureNotFoundError(fixture);
  }
  const payload = await loadFixturePayload(fixture);
  return {
    ...payload,
    data: {
      ...payload.data,
      tenant: payload.data.tenant ?? { id: entry.tenantId, name: entry.tenantName },
    },
  };
}

/**
 * Returns the site marker entries (`map_sites`) for a given fixture.
 *
 * Today this just reads the `data.map_sites` block embedded in the
 * fixture — which mirrors the upstream
 * `wastesense-report.sample-payload.json` schema verbatim. Long term
 * this is likely to be served separately (either bundled with the
 * report payload or from a dedicated `/locations` endpoint) so it's
 * exposed as its own async function with the same fixture-keying
 * contract as `getWasteSenseReport`.
 *
 * Returns an empty array when the fixture has no map_sites block,
 * which keeps the map component's "no data → fall back to list-only"
 * branch trivial.
 */
export async function getWasteSenseSiteLocations(
  fixture: string | undefined,
  seed?: string,
): Promise<readonly WasteSenseMapSite[]> {
  if (!fixture) {
    throw new Error("No fixture specified");
  }
  if (fixture === RANDOM_FIXTURE_ID) {
    const { generateRandomMapSites } = await import("./random/generator");
    return generateRandomMapSites(seed ?? "");
  }
  if (!FIXTURE_BY_NAME.has(fixture)) {
    throw new WasteSenseFixtureNotFoundError(fixture);
  }
  const payload = await loadFixturePayload(fixture);
  return payload.data.map_sites ?? [];
}
